import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, RefreshCw, RotateCcw, Save } from '../../icons';
// 값 = 단추(2026-10-18 · web 레인 W10-24 · 렌즈 Curtis · 결정 D9)
import { Pencil } from '../../icons';
import './leave-quota-edit.css';
import {
  Button, Checkbox, EmptyState, Input, PageHeader, Select, Table,
  type TableColumn,
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import {
  listLeaveQuotas, listLeaveRules, recalcLeaveQuotas, saveLeaveQuotas,
  type LeaveRuleDto, type LeaveRuleRowDto, type QuotaRowDto,
} from '../../api/hr-admin';
import { describeTiers } from '../../lib/leave-rules';
import './AttendanceAdmin.css';
// 「9시간」 — 적립은 분으로 오므로 근태가 시간을 적는 한 가지 방법을 쓴다 (판 B).
import { formatWorkMinutes } from '../../lib/daily-work';

/**
 * 화면 O — **연차 관리**(자동으로 계산하고, 손으로 고친다). 근태 0단계, 2026-08-23.
 *
 * 설계: `docs/attendance-round2-sample.html` 화면 O · 제안 6.
 *
 * 세 칸으로 나누는 이유 — 최종값 하나만 두고 손으로 덮어쓰면 **왜 그 숫자인지 아무도 모르게
 * 된다**. 자동을 남겨 두면 다시 계산해도 조정이 지워지지 않고, 조정만 보면 예외가 몇 명인지
 * 바로 보인다.
 *
 *   자동 = 입사일 × 부여 규칙 (사람이 못 고친다)
 *   조정 = 사람이 넣은 차이 (**사유 필수** · 감사에 남는다)
 *   최종 = 자동 + 조정 (잔여의 출발점)
 *
 * ★ 법정 최소는 바닥이다. 조정으로 자동값보다 적게 만들려는 저장은 서버가 막고
 *   (`BELOW_STATUTORY_MINIMUM`), 휴직·정직처럼 실제로 줄어드는 경우만 "결근·휴직 차감" 으로
 *   그 바닥을 내려간다.
 *
 * ── 정본 채택 (2026-09-06 · 채택 3단계 ③ 근태 · 연차 넷) ─────────────────────────
 * `PageHeader` · `Select` · `Table`(rowHeader) · `Input` · `Checkbox` · `Button` · `EmptyState`.
 *
 * ★★ **왼쪽 칸은 남겼다 — 산문이 아니라 «지금 쓰이는 규칙»이기 때문이다.** 시행일자별 판 ·
 *    근속 구간 · 회사가 더 주는 것은 **회사마다 · 판마다 다른 값**이고, 표의 「자동」이 왜 그
 *    숫자인지 옆에서 대조하며 고치는 자리다(정본 P8 의 「지금 이 순간의 판정」).
 * ★★ 반대로 **「법이 바뀌면 새 판을 만든다」·「조정에는 사유가 필수」·「보상휴가는 다른 통」**
 *    은 늘 같은 말이라 **도움말로** 보냈다. 다만 보상휴가 사유가 기록에만 남는다는 것은
 *    **칸 옆(placeholder)** 에 그대로 둔다 — 적는 순간에 필요한 말이다.
 * ★ 빵부스러기를 「인사」에서 **「근태 › 근태 관리」**로 고쳤다 — 이 화면은 근태 메뉴의 것이다
 *   (판 C 에서 인사 트리를 떠났는데 부제만 옛 자리에 남아 있었다).
 */

interface Draft {
  adjustTotal: number; adjustReason: string; deduction: boolean;
  /**
   * 보상휴가 (2026-08-30 · 판 J) — **연차와 다른 통**이다.
   * ★ 서버는 이 값을 **줄 곳도 쓸 곳도 이미 갖고 있었는데 부여할 자리가 없어 늘 0** 이었다.
   */
  substituteTotal: number; substituteReason: string;
}

const ERRORS: Record<string, string> = {
  REASON_REQUIRED: '조정에는 사유가 필요합니다.',
  BELOW_STATUTORY_MINIMUM: '법정 최소보다 적게 줄 수 없습니다. 결근·휴직 차감이면 차감으로 표시해 주세요.',
  FORBIDDEN: '이 화면은 회사 관리자만 쓸 수 있습니다.',
};

export function LeaveQuotaPage(): JSX.Element {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [rule, setRule] = useState<LeaveRuleDto | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState<string | null>(null);
  const [rules, setRules] = useState<LeaveRuleRowDto[]>([]);
  const [rows, setRows] = useState<QuotaRowDto[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  /*
   * 값 = 단추 (2026-10-18 · web 레인 W10-24 · 렌즈 Curtis · 결정 D9)
   *   깨끗한 줄은 «조정» · «보상휴가» 를 값 글자로 그리고, 그 값이 단추다(키보드로 닿는다).
   *   누른 줄 · 고친 줄(초안이 있는 줄)만 입력틀이 열린다. 초안이 비면(되돌리기 · 저장 뒤 다시 읽기) 모두 닫는다.
   *   ★ 저장 · 되돌리기 · 초안 셈(`drafts` · `changed`)은 종전 그대로다 — 여는 것만 더했다.
   */
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set());
  const [focusKey, setFocusKey] = useState<string | null>(null);
  useEffect(() => { if (Object.keys(drafts).length === 0) setOpened(new Set()); }, [drafts]);
  const isOpen = (userId: string) => opened.has(userId) || Boolean(drafts[userId]);
  const openRow = (userId: string, field: 'adjust' | 'substitute') => {
    setOpened((s) => new Set([...s, userId]));
    setFocusKey(`${userId}:${field}`);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [quotas, ruleList] = await Promise.all([listLeaveQuotas(year), listLeaveRules()]);
      setRule(quotas.rule);
      setEffectiveFrom(quotas.ruleEffectiveFrom);
      setRows(quotas.rows);
      setRules(ruleList.rules);
      setDrafts({});
    } catch {
      setError('연차 현황을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { void load(); }, [load]);

  const draftOf = useCallback((r: QuotaRowDto): Draft => drafts[r.userId] ?? {
    adjustTotal: r.adjustTotal, adjustReason: r.adjustReason ?? '', deduction: r.adjustTotal < 0,
    substituteTotal: r.substituteTotal ?? 0, substituteReason: '',
  }, [drafts]);

  const setDraft = (userId: string, patch: Partial<Draft>) => {
    setDrafts((d) => {
      const row = rows.find((r) => r.userId === userId);
      const base = d[userId] ?? {
        adjustTotal: row?.adjustTotal ?? 0,
        substituteTotal: row?.substituteTotal ?? 0,
        substituteReason: '',
        adjustReason: row?.adjustReason ?? '',
        deduction: (row?.adjustTotal ?? 0) < 0,
      };
      return { ...d, [userId]: { ...base, ...patch } };
    });
  };

  const changed = useMemo(
    () => rows.filter((r) => {
      const d = drafts[r.userId];
      if (!d) return false;
      return d.adjustTotal !== r.adjustTotal
        || (d.adjustReason || '') !== (r.adjustReason ?? '')
        || d.substituteTotal !== (r.substituteTotal ?? 0);
    }),
    [rows, drafts],
  );

  const onSave = async () => {
    if (changed.length === 0) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await saveLeaveQuotas(year, changed.map((r) => {
        const d = draftOf(r);
        // ★★ **바뀐 칸만 보낸다** — 연차 조정을 늘 보내면, 보상휴가만 고쳤는데
        //    조정 사유가 다시 검사에 걸리거나 값이 덮인다.
        const annualChanged = d.adjustTotal !== r.adjustTotal
          || (d.adjustReason || '') !== (r.adjustReason ?? '');
        const subChanged = d.substituteTotal !== (r.substituteTotal ?? 0);
        return {
          userId: r.userId,
          ...(annualChanged ? {
            adjustTotal: d.adjustTotal,
            adjustReason: d.adjustReason.trim() || undefined,
            deduction: d.deduction || undefined,
          } : {}),
          ...(subChanged ? {
            substituteTotal: d.substituteTotal,
            substituteReason: d.substituteReason.trim() || undefined,
          } : {}),
        };
      }));
      setDone('저장했습니다.');
      await load();
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code ?? '';
      setError(ERRORS[code] ?? '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setBusy(false);
    }
  };

  const onRecalc = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const res = await recalcLeaveQuotas(year);
      setDone(`${res.recalculated}명을 다시 계산했습니다 — 조정은 그대로입니다.`);
      await load();
    } catch {
      setError('다시 계산하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const columns: ReadonlyArray<TableColumn<QuotaRowDto>> = useMemo(() => [
    { key: 'name', header: '이름', width: '120px', rowHeader: true, render: (r) => r.name },
    {
      key: 'hireDate',
      header: '입사일',
      width: '104px',
      render: (r) => <span className={r.hireDate ? undefined : 'is-muted'}>{r.hireDate ?? '미설정'}</span>,
    },
    {
      key: 'auto',
      header: '자동',
      width: '64px',
      align: 'right',
      render: (r) => <span className="is-strong">{r.autoTotal === null ? '—' : r.autoTotal}</span>,
    },
    {
      key: 'adjust',
      header: '조정',
      width: '230px',
      render: (r) => {
        const d = draftOf(r);
        if (!isOpen(r.userId)) {
          return (
            <div className="hra__adjust">
              <button type="button" className="lvq__val" aria-label={`${r.name} 조정 고치기`} onClick={() => openRow(r.userId, 'adjust')}>
                <span className="num">{d.adjustTotal > 0 ? `+${d.adjustTotal}` : d.adjustTotal}</span>
                <Pencil size={12} aria-hidden />
              </button>
              {d.adjustReason && <span className="hra__dim">{d.adjustReason}</span>}
            </div>
          );
        }
        return (
          <div className="hra__adjust">
            <Input
              type="number"
              size="sm"
              wrapperClassName="hra__numbox"
              aria-label={`${r.name} 조정`}
              autoFocus={focusKey === `${r.userId}:adjust`}
              value={d.adjustTotal}
              step={0.5}
              onChange={(e) => setDraft(r.userId, { adjustTotal: Number(e.target.value) })}
            />
            {d.adjustTotal !== 0 && (
              <>
                <Input
                  size="sm"
                  wrapperClassName="hra__reasonbox"
                  aria-label={`${r.name} 조정 사유`}
                  placeholder="사유 (필수)"
                  value={d.adjustReason}
                  onChange={(e) => setDraft(r.userId, { adjustReason: e.target.value })}
                />
                {d.adjustTotal < 0 && (
                  <Checkbox
                    label="결근·휴직 차감"
                    checked={d.deduction}
                    onChange={(e) => setDraft(r.userId, { deduction: e.target.checked })}
                  />
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      key: 'total',
      header: '최종',
      width: '64px',
      align: 'right',
      render: (r) => {
        const auto = r.autoTotal;
        const total = auto === null ? null : auto + draftOf(r).adjustTotal;
        return <span className="is-strong">{total === null ? '—' : total}</span>;
      },
    },
    { key: 'used', header: '사용', width: '64px', align: 'right', render: (r) => r.usedDays },
    {
      key: 'remain',
      header: '잔여',
      width: '64px',
      align: 'right',
      render: (r) => {
        const auto = r.autoTotal;
        const total = auto === null ? null : auto + draftOf(r).adjustTotal;
        const remain = total === null ? null : Math.max(0, total - r.usedDays);
        /**
         * ★★★ **넘겨 쓴 만큼을 적는다** (2026-09-11 · A-1).
         *
         *    종전에는 잔여가 0 에서 멈춰서, **딱 맞게 쓴 사람과 이틀을 더 쓴 사람이 이 줄에서
         *    똑같이 「0」** 이었다 — 조정을 넣는 담당자가 보는 바로 그 화면인데.
         *
         * ★★★ **서버의 `overdrawnDays` 를 쓰지 않는다.** 이 화면의 `total` 은 편집 중인
         *    **초안**(자동 + 지금 입력한 조정)이라, 조정을 고치는 동안 서버 값과 어긋난다.
         *    같은 줄에서 초안으로 세야 **저장 전에도** 맞다.
         * ★ 넘겼을 때만 적는다(정본 P7) — 0 이면 아무 말도 안 한다.
         */
        const over = total === null ? 0 : Math.max(0, r.usedDays - total);
        return (
          <>
            <span className={remain === 0 ? 'is-warn' : undefined}>{remain === null ? '—' : remain}</span>
            {over > 0 && <span className="lvq__over">초과 {over}</span>}
          </>
        );
      },
    },
    {
      // ★ 보상휴가는 **연차와 다른 통**이라 칸을 따로 둔다(합치면 잔여가 거짓말한다).
      key: 'substitute',
      header: '보상휴가',
      width: '230px',
      render: (r) => {
        const d = draftOf(r);
        if (!isOpen(r.userId)) {
          return (
            <div className="hra__adjust">
              <button type="button" className="lvq__val" aria-label={`${r.name} 보상휴가 고치기`} onClick={() => openRow(r.userId, 'substitute')}>
                <span className="num">{d.substituteTotal}</span>
                <Pencil size={12} aria-hidden />
              </button>
              <span className="hra__dim">
                {(r.substituteUsed ?? 0) > 0 ? `사용 ${r.substituteUsed}` : '사용 0'}
                {(r.substituteCreditedMinutes ?? 0) > 0 && (
                  <> · 적립 {formatWorkMinutes(r.substituteCreditedMinutes!)}</>
                )}
              </span>
            </div>
          );
        }
        return (
          <div className="hra__adjust">
            <Input
              type="number"
              size="sm"
              wrapperClassName="hra__numbox"
              aria-label={`${r.name} 보상휴가`}
              autoFocus={focusKey === `${r.userId}:substitute`}
              value={d.substituteTotal}
              min={0}
              step={0.5}
              onChange={(e) => setDraft(r.userId, { substituteTotal: Number(e.target.value) })}
            />
            {/* 쓴 만큼은 읽기만 — 부여는 여기서, 사용은 결재에서 생긴다. */}
            <span className="hra__dim">
              {(r.substituteUsed ?? 0) > 0 ? `사용 ${r.substituteUsed}` : '사용 0'}
              {/*
                ★★★ **이 칸은 «조정»이다** (2026-09-06 · 판 B) — 휴일대체(§57)가 승인되면
                   원장에 따로 쌓이고, 직원의 잔여는 **둘을 더한 값**이다. 안 적으면 담당자는
                   「0일」을 보는데 직원에게는 9시간이 있는 화면이 된다.
                ★ 쌓인 것이 없으면 안 뜬다 — 정책이 꺼진 회사·옛 서버가 그렇다.
              */}
              {(r.substituteCreditedMinutes ?? 0) > 0 && (
                <> · 적립 {formatWorkMinutes(r.substituteCreditedMinutes!)}</>
              )}
            </span>
            {d.substituteTotal !== (r.substituteTotal ?? 0) && (
              // ★ 「기록에만 남는다」는 **적는 순간에 필요한 말**이라 칸 옆에 남는다(도움말이 아니다).
              <Input
                size="sm"
                wrapperClassName="hra__reasonbox"
                aria-label={`${r.name} 보상휴가 사유`}
                placeholder="사유 (기록에만 남습니다)"
                value={d.substituteReason}
                onChange={(e) => setDraft(r.userId, { substituteReason: e.target.value })}
              />
            )}
          </div>
        );
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [draftOf, rows, opened, drafts, focusKey]);

  return (
    <div className="hra">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/leave')}
        scope={scopeFor('/erp/attendance/leave')}
        title="연차 관리"
        description={`${year}년 부여 ${rows.length}명 · 자동으로 계산한 뒤 손으로 고칩니다.`}
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p><b>자동</b>은 입사일 × 부여 규칙이라 사람이 못 고칩니다. <b>조정</b>은 사람이 넣은
                차이이고, <b>최종 = 자동 + 조정</b>이 잔여의 출발점입니다. 최종값 하나만 두고 덮어쓰면
                <b> 왜 그 숫자인지 아무도 모르게 됩니다</b>.</p>
              <p><b>조정에는 사유가 필수</b>이고 감사에 남습니다. 법정 최소는 바닥이라 자동값보다
                적게 만드는 저장은 서버가 막습니다 — 휴직·정직처럼 실제로 줄어드는 경우만
                <b> 「결근·휴직 차감」</b>으로 그 바닥을 내려갑니다.</p>
              <p><b>보상휴가는 연차와 다른 통</b>이라 잔여에 섞이지 않습니다. 부여는 이 화면에서,
                사용은 결재에서 생깁니다. 사유는 <b>기록에만</b> 남고 이 표에는 다시 보이지 않습니다.</p>
              <p>법이 바뀌면 <b>규칙을 새로 만들고 시행일자를 붙입니다</b>. 기존 규칙을 고치지
                않습니다 — 고치면 이미 부여된 과거 연도가 함께 바뀝니다.</p>
            </>
          ),
        }}
        actions={(
          <Select
            ariaLabel="연도 선택"
            size="sm"
            value={String(year)}
            onChange={(v) => setYear(Number(v))}
            options={[year + 1, year, year - 1, year - 2].map((y) => ({ value: String(y), label: `${y}년` }))}
          />
        )}
      />

      <div className="hra__split">
        {/*
          ★★ 이 칸은 **산문이 아니라 값**이다 — 회사마다 · 판마다 다르고, 표의 「자동」이 왜 그
          숫자인지 옆에서 대조하는 자리다. 그래서 도움말로 보내지 않는다(정본 P8).
        */}
        <aside className="hra__aside" aria-label="부여 규칙">
          <h2 className="hra__rulehead">부여 규칙 <span className="hra__dim">시행일자별</span></h2>
          <ul className="hra__versions">
            {rules.length === 0 && !loading && <li className="hra__dim">규칙이 없습니다 — 법정 최소로 계산합니다.</li>}
            {rules.map((r) => (
              <li key={r.id} className={r.isCurrent ? 'is-current' : undefined}>
                <b>{r.effectiveFrom} 시행</b>
                <span>{r.isCurrent ? '사용 중' : '보관'}</span>
              </li>
            ))}
          </ul>

          {rule && (
            <>
              <h3 className="hra__rulehead">근속 구간 · 일수</h3>
              <ul className="hra__rulelist">
                {describeTiers(rule).map((line) => <li key={line}>{line}</li>)}
              </ul>
              <h3 className="hra__rulehead">회사가 더 주는 것</h3>
              <ul className="hra__rulelist">
                <li>첫 해 가산 {rule.firstYearBonus}일</li>
                <li>이월 {rule.carryOver ? '허용' : '미허용 — 끄면 연말에 소멸'}</li>
                <li>부여 방식 {rule.basis === 'FISCAL_YEAR' ? '회계연도 기준' : '입사일 기준'}</li>
              </ul>
              {/* 「지금 어느 판으로 계산했나」는 값이라 화면에 남는다. */}
              {effectiveFrom && <p className="hra__note">지금 적용: <b>{effectiveFrom}</b></p>}
            </>
          )}
        </aside>

        <section className="hra__main">
          {error && <p className="hra__error" role="alert"><AlertCircle size={14} aria-hidden />{error}</p>}
          {done && !error && <p className="hra__ok" role="status"><Check size={14} aria-hidden />{done}</p>}

          <Table
            caption={`${year}년 연차 부여`}
            className="hra-tbl"
            columns={columns}
            rows={rows}
            rowKey={(r) => r.userId}
            loading={loading}
            // 고친 줄은 눈에 남는다 — 저장 전에 무엇을 건드렸는지가 보여야 한다.
            rowClassName={(r) => (drafts[r.userId] ? 'is-changed' : undefined)}
            empty={<EmptyState title="직원이 없습니다." description="입사일이 등록된 직원이 있어야 연차를 부여합니다." />}
          />

          <footer className="hra__foot">
            <div className="hra__actions">
              <Button variant="secondary" iconLeft={<RefreshCw size={14} aria-hidden />} onClick={() => { void onRecalc(); }} disabled={busy}>
                전원 자동 계산
              </Button>
              <Button variant="secondary" iconLeft={<RotateCcw size={14} aria-hidden />} onClick={() => setDrafts({})} disabled={changed.length === 0 || busy}>
                되돌리기
              </Button>
              <Button iconLeft={<Save size={14} aria-hidden />} onClick={() => { void onSave(); }} disabled={changed.length === 0 || busy} loading={busy}>
                저장
              </Button>
            </div>
          </footer>
        </section>
      </div>
    </div>
  );
}

export default LeaveQuotaPage;
